/**
 * Beat 4's authored content: three rounds, a nose, some water, and a door.
 *
 * Same file for beat 4 that `latchScene.ts` is for beat 2 — the numbers that
 * *are* the beat, kept away from the machinery that runs them. **It has no
 * renderer dependency and must not acquire one**, so a spec can assert against a
 * patrol route in Node without booting a browser, which is how `patrol.spec.ts`
 * and `worker-route.spec.ts` already work.
 *
 * ── The one thing to understand before changing a number here ───────────────
 *
 * `docs/beat-4-plan.md`: **this beat's cover is a clock.** The corridor is lit
 * end to end and has three bays and one pipe lane in thirty metres, so the
 * question the player is answering is never *where* but *when*. Every dwell and
 * every `restBetweenRounds` below is therefore content in a way that a walking
 * speed in a hall is not: shortening the checker's twelve seconds does not make
 * the room slightly easier, it deletes the reason the corner is dangerous.
 *
 * Tune them by playing with a stopwatch, which is what `STATUS.md` has had as a
 * task since session 15 and what none of these numbers has had yet.
 */

import type { PatrolDefinition } from '@/systems/patrol';
import type { WorkerRoute } from '@/systems/worker';

/**
 * Where all three men are when they are not in the corridor.
 *
 * Through the personnel door at (12, 9) and two metres beyond it, so a round
 * begins and ends with a man **coming through a door** rather than appearing at
 * the edge of the room. It is also why the door opening is an event that happens
 * to the corridor rather than at its boundary: they use it, constantly, and she
 * watches them do it long before it is her turn.
 */
export const OFFSTAGE = { x: 13.6, z: 9.0 } as const;

/**
 * The walker — the metronome. End to end, no dwell but the one at the far end.
 *
 * He is what the crossing is timed against, so he is the fastest of the three and
 * the only one who covers the whole room. His round is about eighty seconds,
 * which means roughly thirty-five seconds between him passing a bay going away
 * and returning — the window a five-metre hop between bays has to fit inside.
 *
 * There is no exit waypoint. After the last one `Worker` returns him to `park`,
 * and that walk *is* him going back out through the door.
 */
export function walkerRoute(): WorkerRoute {
  return {
    park: OFFSTAGE,
    speed: 0.9,
    turnRate: 3.1,
    stride: 0.78,
    restBetweenRounds: 10,
    waypoints: [
      // The pressure mat, inbound. The door opens for him here, and this is the
      // first of the two times a round it does so with nothing said about it.
      { x: 11.2, z: 9.0, dwell: 0 },
      { x: 1.9, z: 9.0, dwell: 0 },
      { x: 0.0, z: 8.2, dwell: 0 },
      // The near end, by the door she came in through. Two seconds facing it,
      // which is just long enough to be a man checking a door and not long
      // enough to be a dwell she can plan around.
      { x: 0.0, z: -7.9, dwell: 2.0, facing: 0 },
      { x: 0.0, z: 8.2, dwell: 0 },
      // And the mat again, outbound.
      { x: 11.2, z: 9.0, dwell: 1.2, facing: Math.PI / 2 },
    ],
  };
}

/**
 * The checker — the reason the dog-leg is not free.
 *
 * Twelve seconds standing still at the corner, facing down eighteen metres of lit
 * corridor. That is this beat's hardest single fact and it is the one thing a
 * cone plus a dwell does better than any scripted trick: he is not hunting her,
 * he is looking, and looking is enough.
 *
 * Waypoint 2 is at x 0.5 rather than out in the short leg, and the half metre
 * matters — from anywhere further along, the short leg's south wall blinds him
 * and the dwell becomes decoration.
 */
export function checkerRoute(): WorkerRoute {
  return {
    park: OFFSTAGE,
    speed: 0.62,
    turnRate: 2.6,
    stride: 0.62,
    restBetweenRounds: 16,
    waypoints: [
      { x: 11.2, z: 9.0, dwell: 0 },
      // Something on the south wall, halfway along. Five seconds, and its real
      // job is to make the twelve at the corner read as *his habit* rather than
      // as the room having one trap in it.
      { x: 5.6, z: 9.0, dwell: 5, facing: Math.PI },
      // The corner. `Math.PI` is straight down the long leg (0 faces +Z).
      { x: 0.5, z: 9.3, dwell: 12, facing: Math.PI },
      { x: 11.2, z: 9.0, dwell: 0 },
    ],
  };
}

/**
 * The handler — the dog walks with him, and he is who opens the door at the end.
 *
 * Waypoint 2 is level with the wash-down bay's south edge, and **`facing: Math.PI`
 * points him down the long leg, away from the bay mouth.** That is deliberate and
 * must not be "corrected" to face the hose he is nominally checking: she is in
 * that bay during stage 5, and a man who dwells staring into her only cover makes
 * the stage unfair. The threat in stage 5 is the dog.
 *
 * Waypoint 4 is the mat, and its two and a half seconds plus `DOOR.holdAfterRelease`
 * is the entire window stage 7 is built on. His round repeats, so the window
 * repeats — which is why that stage can afford to have no patience clock.
 */
export function handlerRoute(): WorkerRoute {
  return {
    park: OFFSTAGE,
    speed: 0.7,
    turnRate: 2.8,
    stride: 0.68,
    restBetweenRounds: 24,
    waypoints: [
      { x: 11.2, z: 9.0, dwell: 0 },
      { x: 0.0, z: 8.5, dwell: 0 },
      { x: 0.0, z: 5.2, dwell: 10, facing: Math.PI },
      { x: 0.0, z: 8.5, dwell: 0 },
      { x: 11.2, z: 9.0, dwell: 2.5, facing: Math.PI / 2 },
    ],
  };
}

/**
 * What each of them can see.
 *
 * Narrow cones and short ranges, and both are generosity rather than realism —
 * the same argument `latchScene.ts` makes about beat 2's worker. The checker is
 * the exception at twelve metres, and that number is an authored answer to *how
 * far down the corridor is the corner dangerous*: from the corner it reaches to
 * about z −2.7, which is past the bin bay and short of the junction bay. That is
 * what gives the crossing a far half and a near half.
 *
 * **The order of these three is load-bearing.** `CorridorDirector` indexes them:
 * 0 the walker, 1 the checker, 2 the handler.
 */
export const WALKER: PatrolDefinition = {
  id: 'walker',
  route: walkerRoute(),
  coneHalfAngle: 0.45,
  range: 9.0,
  eyeHeight: 1.62,
};

export const CHECKER: PatrolDefinition = {
  id: 'checker',
  route: checkerRoute(),
  coneHalfAngle: 0.42,
  range: 12.0,
  eyeHeight: 1.60,
};

export const HANDLER: PatrolDefinition = {
  id: 'handler',
  route: handlerRoute(),
  coneHalfAngle: 0.50,
  range: 8.0,
  eyeHeight: 1.64,
};

/**
 * The dog.
 *
 * `speed` is below her run and above her walk, **on purpose**. The plan's rule 3:
 * *"The dog is not fast and does not need to be. It is inevitable, which is
 * worse."* A dog that can be outrun is a dog the player solves with the roll, and
 * then the hose means nothing.
 *
 * `noseRadius` is deliberately absent so the tracker takes `SCENT_TUNING`'s
 * default — the radius is a property of what a nose is, not of this dog.
 */
export const DOG = {
  speed: 1.15,
  turnRate: 2.4,
  /** Seconds of casting with nothing to smell before it gives up. */
  giveUpSeconds: 14,
  catchRadius: 0.55,
  /** Where it walks relative to the handler, in his own frame. */
  heelBehind: 0.55,
  heelLeft: 0.35,
  /**
   * Metres between claw ticks.
   *
   * Per distance travelled and not per second, for the reason every gait in this
   * project is: a sound emitted on a timer slides against the animal that made
   * it the moment its speed changes, and this one changes speed twice — heeling
   * behind a man at 0.7 m/s and hunting at 1.15.
   *
   * One tick per stride rather than one per paw. Four feet at this distance is a
   * texture nobody can count, and the plan's dog is *inevitable*, which is a
   * steady sound rather than a busy one.
   */
  stride: 0.42,
  /** Seconds between snuffs while the nose is down. Slow: it is reading, not searching. */
  castEvery: 2.4,
} as const;

/**
 * The water.
 *
 * Spec §7.4 says *"a hose to **drag** over a drain"* and `drag` does not exist;
 * see the beat's own header and `docs/beat-4-plan.md` rule 5 for why this is a
 * nudge instead. The valve is old, the nozzle is already lying on the lip of the
 * gully, and a shove is enough.
 *
 * **There is no shut-off.** Once it is running the floor stays wet for the rest
 * of the beat, which is both the honest behaviour of a jammed valve and the thing
 * that makes the last stage survivable.
 */
export const WATER = {
  /** Where the valve is. The nozzle lies on the lip of the gully. */
  source: { x: -2.2, z: 6.0 },
  /** Seconds from the nudge to the patch reaching its full extent. */
  spreadSeconds: 8,
  /**
   * What it covers when it has finished spreading.
   *
   * It reaches x 1.2 — the corridor's far wall — deliberately. The lane she has
   * to cross is the lane that has to get wet, and a patch that stayed inside the
   * bay would let the dog pick her up again the moment she stepped out of it.
   */
  full: { minX: -3.0, maxX: 1.2, minZ: 3.6, maxZ: 7.4 },
} as const;

/**
 * The pressure mat and the door it opens.
 *
 * `boots` is documentation and is deliberately never read by a computation. Only
 * the three men load this mat; Rosalie standing on it opens nothing, and there
 * is no code path by which she could. **That is not a gap to be filled** — it
 * is the beat's whole thesis, that she goes through on somebody else's timing.
 * What her weight does get is an answer: the latch knocks and the leaf shivers
 * shut (`CorridorDirector.#stepDoor`, audit row 12).
 */
export const DOOR = {
  /** Half-extent of the mat's trigger box in XZ, centred on `MAT_AT`. */
  matHalf: { x: 0.45, z: 0.55 },
  /** Seconds the leaf takes to swing, either way. */
  swingSeconds: 0.9,
  /** Seconds the door stays open after the last load leaves the mat. Stage 7 is this number. */
  holdAfterRelease: 3.0,
  /** Radians the leaf opens through. */
  openAngle: Math.PI / 2,
  /** Kilograms the mat wants. She does not weigh it and never will. */
  boots: 55,
} as const;
